import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { 
  useCustomerStore,
  usePaymentMethodsStore,
  useSubscriptionsStore,
  initializeSubscriptionData
} from '@/stores/subscription'

export function useSubscriptionActions() {
    const router = useRouter()
    const customerStore = useCustomerStore()
    const paymentMethodsStore = usePaymentMethodsStore()
    const subscriptionsStore = useSubscriptionsStore()

    const isSubscriptionActionLoading = ref(false)
    const subscriptionActionError = ref<string | null>(null)

    const extractErrorMessage = (error: any, fallback: string) => {
        return error?.response?.data?.error || error?.response?.data?.detail || error?.message || fallback
    }

    const ensureStripeCustomer = async () => {
        const customer = customerStore.customer
        if (customer?.stripe_customer_id) {
            return customer
        }

        try {
            return await customerStore.createCustomer()
        } catch (error: any) {
            throw new Error(extractErrorMessage(error, 'Failed to create customer'))
        }
    }

    const openCheckoutSession = async <T extends { session_url?: string }>(
        fetchSession: () => Promise<T>
    ) => {
        const session = await fetchSession()
        const url = session?.session_url
        if (!url) {
            throw new Error('No session URL received from server')
        }
        window.location.href = url
    }

    const handleReactivateSubscription = async () => {
        try {
            await initializeSubscriptionData()
        } catch (error: any) {
            throw new Error(error.message || 'Failed to reactivate subscription')
        }
    }

    const handleRetryPayment = async () => {
        subscriptionActionError.value = null
        isSubscriptionActionLoading.value = true

        try {
            const result = await subscriptionsStore.retryPayment()

            if (result?.checkout_url) {
                window.open(result.checkout_url, '_blank')
            } else {
                subscriptionActionError.value = 'No checkout URL received from server'
            }
        } catch (error: any) {
            subscriptionActionError.value = extractErrorMessage(error, 'Failed to retry payment')
        } finally {
            isSubscriptionActionLoading.value = false
        }
    }

    const createSubscriptionCheckout = async (planId: number) => {
        subscriptionActionError.value = null
        isSubscriptionActionLoading.value = true

        try {
            await ensureStripeCustomer()
            await paymentMethodsStore.fetchPaymentMethods()
            const customerId = customerStore.customer?.stripe_customer_id
            const defaultPm = paymentMethodsStore.paymentMethods.find(pm => pm.is_default) || paymentMethodsStore.paymentMethods?.[0]

            if (customerId && defaultPm) {
                try {
                    await paymentMethodsStore.setDefaultPaymentMethod(String(defaultPm.id), customerId)
                } catch (_e) {
                    // Non-blocking
                }
            }

            await openCheckoutSession(() => subscriptionsStore.createCheckoutSession(
                planId,
                undefined,
                {
                    customerId,
                    paymentMethodId: defaultPm?.stripe_payment_method_id
                }
            ))
        } catch (error: any) {
            subscriptionActionError.value = extractErrorMessage(error, 'Failed to start checkout session')
        } finally {
            isSubscriptionActionLoading.value = false
        }
    }

    return {
        isSubscriptionActionLoading,
        subscriptionActionError,
        ensureStripeCustomer,
        handleReactivateSubscription,
        handleRetryPayment,
        createSubscriptionCheckout,
        openCheckoutSession,
        extractErrorMessage
    }
}
